import {EnvGlobalsHandler} from "../../EnvGlobalsHandler";
import {IEnvironmentGlobals} from "../../IEnvironmentGlobals";
import {IdentityManagementConstants} from "../../constants/IdentityManagementConstants";
import {BasicController} from "../../controller/BasicController";
import {LicenseRefresher} from "../../controller/LicenseRefresher";
import {IdentityManagementEvent} from "../../event/IdentityManagementEvent";
import {IdentityManagementLicenseEvent} from "../../event/IdentityManagementLicenseEvent";
import {BasicModel} from "../BasicModel";
import {CommandController} from "./../../../commanding/CommandController";
import {getLogger} from "@goodgamestudios/logger";
import {commentedOut} from "../../../../../decorators";

import Stage = createjs.Stage;
import {ExternalInterface} from "@goodgamestudios/createjs-utilities";
import BlurFilter = createjs.BlurFilter;

const logger = getLogger("CoreJS.BasicIdentityManagementData");
export class BasicIdentityManagementData {
    private _licenseRefresher: LicenseRefresher;
    public showStuff: boolean = false;

    public initialize(): void {
        // Create license refresher
        this.initializeLicenseRefresher();
    }

    public authenticateViaJS(): void {
        // Listen for Javascript callback from ID-Check HTML popup
        ExternalInterface.addCallback("triggerLogin", this.bindFunction(this.triggerLogin));
        ExternalInterface.addCallback("setIDCheckData", this.bindFunction(this.setIdCheckData));

        // Now that our callback mechanism is all set we can actually trigger
        // the JS code which will open the age check, passing PING to it
        let playerID: number = BasicModel.userData.playerID;
        let instanceID: number = BasicModel.instanceProxy.selectedInstanceVO.instanceId;
        let networkId: number = EnvGlobalsHandler.globals.networkId;
        let gameId: number = EnvGlobalsHandler.globals.gameId;
        this.startAgeCheck(playerID, instanceID, networkId, gameId);
    }

    public unregisterJSCallbacks(): void {
        // Remove listener for Javascript callback from ID-Check HTML popup

        // TODO investigate impact of this.  Currently there are no other use cases for removeCallbac - old implementation was just passing null but with that approach in case of a callback trigger a " Uncaught TypeError: myCallback is not a function" would be thwron.
        // ( calling window[triggerLogin]() with it being
        ExternalInterface.addCallback("triggerLogin", () => {
            logger.debug(" triggerLogin callback was removed. ignoring event");
        });
        ExternalInterface.addCallback("setIDCheckData", () => {
            logger.debug("setIDCheckData callback was removed.  ignoring event ");
        });
    }

    /**
     * Calls the Javascript method that will open the pop-up with the age
     * verification stuff.
     * @param playerId
     * @param instance
     * @param network
     * @param gameId
     * @param screenShot Screenshot data in JPG format as Base64 String
     */
    private startAgeCheck(playerId: number, instance: number, network: number, gameId: number, screenShot: string = null): void {

        if (screenShot) {
            ExternalInterface.call("ggsOpenSirenAuthentification", playerId, instance, network, gameId, screenShot);
        }
        else {
            ExternalInterface.call("ggsOpenSirenAuthentification", playerId, instance, network, gameId);
        }

    }

    /**
     * Called by Javascript on successful authentication from user.
     * Triggers a login on the client
     */
    private triggerLogin(/*data:Object*/): void {
        this.unregisterJSCallbacks();
        // We will re-trigger a login command at this stage
        CommandController.instance.executeCommand(BasicController.COMMAND_LOGIN);
    }

    /**
     * Called by Javascript on failed authentication from user.
     */
    private setIdCheckData(data: any): void {
        let errorCodes: string = data.error;
        if (!errorCodes) {
            return;
        }
        let errorJson: any;
        try {
            errorJson = JSON.parse(errorCodes);
        } catch (e) {
            logger.warn("[BasicIdentityManagementData] JSON decoding error occured!");
            CommandController.instance.executeCommand(BasicController.COMMAND_HANDLE_IDENTITY_MANAGEMENT_ERROR, IdentityManagementConstants.ERROR_VERIFICATION_FAILED);
            return;
        }
        // Get "errors" array
        errorJson = (errorJson) ? errorJson.errors[0] : {};
        if (errorJson.name && errorJson.type) {
            CommandController.instance.executeCommand(BasicController.COMMAND_HANDLE_IDENTITY_MANAGEMENT_ERROR, IdentityManagementConstants.ERROR_VERIFICATION_FAILED);
            return;
        }
    }

    public get isUserAuthenticated(): boolean {
        return this.env.identityManagementId != "";
    }

    private initializeLicenseRefresher(): void {
        this._licenseRefresher = new LicenseRefresher(
            IdentityManagementConstants.LICENSE_REFRESHER_INITIAL_SHOW_DELAY_SECONDS,
            IdentityManagementConstants.LICENSE_SHOW_SCHEDULE_MINUTES,
            IdentityManagementConstants.LICENSE_DISPLAY_DURATION_SECONDS
        );
        this._licenseRefresher.licenseAgeRatingShowed.add(this.bindFunction(this.onLicenseAgeRatingShow));
        this._licenseRefresher.licenseShowed.add(this.bindFunction(this.onLicenseShow));
        this._licenseRefresher.licenseHided.add(this.bindFunction(this.onLicenseHide));
        this._licenseRefresher.startRefreshing();
    }

    private onLicenseAgeRatingShow(): void {
        let e: IdentityManagementEvent = new IdentityManagementEvent(IdentityManagementLicenseEvent.SHOW_LICENSE_INITIAL_AGE_RATING_TEXT);
        BasicController.getInstance().dispatchEvent(e);
    }

    private onLicenseShow(): void {
        let e: IdentityManagementEvent = new IdentityManagementEvent(IdentityManagementLicenseEvent.SHOW_LICENSE_LOGOS);
        BasicController.getInstance().dispatchEvent(e);
    }

    private onLicenseHide(): void {
        BasicController.getInstance().dispatchEvent(new IdentityManagementEvent(IdentityManagementLicenseEvent.HIDE_LICENSE));
    }

    public startLicenseRefresher(): void {
        if (!this._licenseRefresher) {
            logger.warn("licenseRefresher is null");
            return;
        }
        this._licenseRefresher.startRefreshing();
    }

    public resetLicenseTimer(): void {
        this._licenseRefresher.startRefreshing();
        this._licenseRefresher.onLicenseShowScheduleIntervalReached();
    }

    public stopLicenseRefresher(): void {
        if (!this._licenseRefresher) {
            logger.warn("licenseRefresher is null");
            return;
        }
        this._licenseRefresher.stopRefreshing();
    }

    public dispose(): void {
        this.unregisterJSCallbacks();
        this.env.identityManagementId = null;
    }

    private get env(): IEnvironmentGlobals {
        return EnvGlobalsHandler.globals;
    }
}
